import { toField } from "../fields/fields";
import SubformService from "../services/SubformService";
import FormServicesFactory from "../services/FormServicesFactory";
import type FieldId from "../fields/FieldId";
import type { FieldType } from "../types";
import { Scope } from "../types";
import { t, type TFunction } from "i18next";
import { isEqual, isNil } from "lodash-es";
import { CURRENCY_NAMESPACE } from "../state/i18n";
import { ALLOWED_CONDITION_FIELD_TYPES, ALLOWED_CONDITION_VARIABLE_TYPES } from "../constants";
import { type SubformFieldProperties } from "../fields/subform/SubformField";
import { type PinFieldProperties } from "../fields/media/PinField";
import type { FormScope } from "../state/formVersion";
import { ISOCurrency } from "../currency";
import type { FieldConfig, VariableConfig } from "../fields/types";
import VariableService from "../services/VariableService";
import type { VariableType } from "../variables/types";
import VariableValueReference from "../variables/VariableValueReference";
import { createDynamicValueOptions } from "./menu/searchableMenuUtil";
import type { DynamicValueId, DynamicValueOption, SelectOption, SelectOptions } from "../types/Menu";
import { getDynamicValueIcon } from "./menu/optionIconUtil";
import MetaVariableReference from "../metaVariables/MetaVariableReference";
import type { MetaVariableId } from "../metaVariables/metaVariable";
import { META_VARIABLES } from "../metaVariables/metaVariable";

export const metaVariableOption = (metaVariableId: MetaVariableId): DynamicValueOption<DynamicValueId> => {
  const id = MetaVariableReference.from(metaVariableId);
  const icon = getDynamicValueIcon(metaVariableId, metaVariableId);

  return {
    variant: "dynamicValue",
    value: id,
    label: t(META_VARIABLES[metaVariableId].label),
    icon,
  };
};

export const toFieldOption = (field: FieldConfig): DynamicValueOption<FieldId> => {
  const fieldType = toField(field.type);
  const icon = getDynamicValueIcon(field.uid, field.type);

  return {
    variant: "dynamicValue",
    value: field.uid,
    label: fieldType.displayLabel(field.properties),
    pill: field.properties.data_name as string,
    icon,
  };
};

export const toFieldValueOptions = (
  field: FieldConfig,
  options?: Partial<Record<FieldType, Record<string, unknown>>>,
): DynamicValueOption<DynamicValueId>[] =>
  toField(field.type)
    .valueReferenceOptions(field.uid, field.properties, options)
    .filter(({ isSelectable }) => isSelectable !== false);

export const toVariableValueOption = (variable: VariableConfig): DynamicValueOption<DynamicValueId> => {
  const id = VariableValueReference.from(variable.uid.toString());
  const icon = getDynamicValueIcon(id, variable.type);

  return {
    variant: "dynamicValue",
    value: id,
    label: variable.name,
    pill: variable.dataName,
    icon,
  };
};

export const createOptionsForCalculationTerms = (
  fieldId: FieldId,
  scope: FormScope,
  allowedFieldTypes: FieldType[],
  allowedVariableTypes: VariableType[],
  t: TFunction,
): SelectOptions<DynamicValueId> => {
  const fields =
    scope === Scope.ROOT
      ? FormServicesFactory.forScope(Scope.ROOT)
          .fieldService.getFieldsByTypes(allowedFieldTypes)
          .filter((field) => !field.uid.equals(fieldId))
          .flatMap((field) => toFieldValueOptions(field))
      : [];

  const subforms = SubformService.getSubformsReachableFrom(scope)
    .flatMap((subform) => {
      const isCurrentSubform = subform.uid === scope;

      return {
        label: isCurrentSubform ? t(($) => $.MENU_SECTION_SUBFORMS_CURRENT) : subform.meta.name,
        subformId: subform.uid,
        entries: FormServicesFactory.forSubform(subform.uid)
          .fieldService.getFieldsByTypes(allowedFieldTypes)
          .filter((field) => !field.uid.equals(fieldId))
          .flatMap((field) => toFieldValueOptions(field)),
      };
    })
    // Ensure current subform is first one in the array
    .sort((a) => (a.subformId.equals(scope.toString()) ? -1 : 0))
    .map((a) => ({ [a.label]: a.entries }));

  const customVariables = FormServicesFactory.forScope(scope)
    .variableService.getVariablesByTypes(allowedVariableTypes)
    .map(toVariableValueOption);

  return createDynamicValueOptions(t, {
    fields,
    subforms,
    metaVariables: [],
    customVariables,
  });
};

export const toPlaceholderFieldOption = (field: FieldConfig): DynamicValueOption<DynamicValueId> => {
  const fieldType = toField(field.type);
  const icon = getDynamicValueIcon(field.uid, field.type);

  return {
    variant: "dynamicValue",
    value: field.uid,
    label: fieldType.displayLabel(field.properties),
    pill: field.properties.data_name as string,
    icon,
  };
};

export const toPlaceholderVariableOption = (variable: VariableConfig): DynamicValueOption<DynamicValueId> => {
  const icon = getDynamicValueIcon(variable.uid, variable.type);

  return {
    variant: "dynamicValue",
    value: variable.uid,
    label: variable.name,
    pill: variable.dataName,
    icon,
  };
};

export const createOptionsForRuleConditions = (scope: FormScope, t: TFunction): SelectOptions<DynamicValueId> => {
  const isSubform = scope !== Scope.ROOT;
  const parents = isSubform ? SubformService.getScopedParents(scope) : [];
  const fields =
    !isSubform || parents.some((parent) => parent.scope === Scope.ROOT)
      ? FormServicesFactory.forScope(Scope.ROOT)
          .fieldService.getFieldsByTypes(ALLOWED_CONDITION_FIELD_TYPES)
          .map(toPlaceholderFieldOption)
      : [];

  const subforms = parents
    .filter(
      (parent): parent is { scope: FormScope; subform: NonNullable<(typeof parent)["subform"]> } =>
        !isNil(parent.subform),
    )
    .map(({ scope: parentScope, subform }) => ({
      id: subform.uid.toString(),
      label: isSubform && scope.equals(parentScope) ? t(($) => $.MENU_SECTION_SUBFORMS_CURRENT) : subform.meta.name,
      entries: FormServicesFactory.forSubform(subform.uid)
        .fieldService.getFieldsByTypes(ALLOWED_CONDITION_FIELD_TYPES)
        .filter(isNotReferencingSelf(scope))
        .map(toPlaceholderFieldOption),
    }))
    // Ensure current subform is first one in the array
    .sort((subform) => (subform.id === scope.toString() ? -1 : 0))
    .map((a) => ({ [a.label]: a.entries }));

  const customVariables = VariableService.getVariablesByTypes(ALLOWED_CONDITION_VARIABLE_TYPES).map(
    toPlaceholderVariableOption,
  );

  return createDynamicValueOptions(t, {
    fields,
    subforms,
    metaVariables: [metaVariableOption("info.userId")],
    customVariables,
  });
};

const isNotReferencingSelf = (scope: FormScope) => (field: FieldConfig) => {
  if (field.type === "subform") {
    const subformField = field as FieldConfig<SubformFieldProperties>;
    return !isEqual(scope.toString(), subformField.properties.target_form_id);
  }
  if (field.type === "pin") {
    const pinField = field as FieldConfig<PinFieldProperties>;
    return !pinField.properties.pins.some((pin) => !isEqual(scope.toString(), pin.target_form_id));
  }

  return true;
};

export const createOptionsForCurrencies = (t: TFunction): SelectOption<string>[] =>
  Object.entries(ISOCurrency)
    .map(([key, currency]) => ({
      variant: "text" as const,
      value: key,
      label: `${t(key as unknown as Parameters<typeof t>[0], { ns: CURRENCY_NAMESPACE, defaultValue: currency.name })}${currency.symbol ? " (" + currency.symbol + ")" : ""}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
